import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useEffect, useState } from "react";

import { AuthMenu } from "@/components/AuthMenu";
import { useCurrentUser } from "@/hooks/useAuth";
import {
  type AnalyticsRange,
  useAnalytics,
} from "@/hooks/useAnalytics";

const RANGES: { value: AnalyticsRange; label: string }[] = [
  { value: "24h", label: "24h" },
  { value: "7d", label: "7d" },
  { value: "30d", label: "30d" },
];

export function AdminAnalyticsPage() {
  const navigate = useNavigate();
  const { data: user, isLoading: authLoading } = useCurrentUser();
  const [range, setRange] = useState<AnalyticsRange>("7d");
  const { data, isLoading, error } = useAnalytics(range);

  useEffect(() => {
    if (authLoading) return;
    if (!user || !user.is_admin) {
      navigate("/", { replace: true });
    }
  }, [authLoading, user, navigate]);

  if (authLoading || !user || !user.is_admin) return null;

  const maxSeries = data?.series.reduce((m, p) => Math.max(m, p.hits), 0) ?? 0;
  const maxCountry = data?.countries.reduce((m, c) => Math.max(m, c.hits), 0) ?? 0;

  return (
    <>
      <Helmet>
        <title>Analytics — Admin</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      <div className="min-h-screen bg-surface-0 flex flex-col">
        <div className="sticky top-0 z-30 bg-surface-0/95 backdrop-blur border-b border-surface-2 px-4 py-3">
          <div className="max-w-5xl mx-auto flex flex-wrap items-center gap-x-3 gap-y-2">
            <Link
              to="/"
              className="text-sm text-zinc-400 hover:text-accent-light min-h-[40px] inline-flex items-center"
            >
              ← Search
            </Link>
            <h1 className="text-sm font-semibold">Analytics</h1>
            <span className="text-[10px] uppercase tracking-wider text-accent-light">
              admin
            </span>
            <div className="ml-auto">
              <AuthMenu />
            </div>
            <div className="flex bg-surface-2 rounded text-xs w-full sm:w-auto sm:ml-2">
              {RANGES.map((r) => (
                <button
                  key={r.value}
                  onClick={() => setRange(r.value)}
                  className={`flex-1 sm:flex-none px-3 py-2 min-h-[40px] transition-colors ${
                    range === r.value
                      ? "bg-accent text-white rounded"
                      : "text-zinc-400 hover:text-white"
                  }`}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex-1 px-4 py-6 max-w-5xl mx-auto w-full space-y-6">
          {error && (
            <div className="bg-red-900/30 border border-red-800 text-red-200 text-sm rounded p-3">
              Failed to load analytics: {(error as Error).message}
            </div>
          )}

          {isLoading || !data ? (
            <div className="space-y-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="bg-surface-2 h-24 rounded animate-pulse" />
              ))}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3">
                <Tile label="Total queries" value={data.total_queries.toLocaleString()} />
                <Tile
                  label="Distinct sessions"
                  value={data.distinct_sessions.toLocaleString()}
                />
              </div>

              <Section title="Volume (per hour, UTC)">
                {data.series.length === 0 ? (
                  <Empty>No queries in this window yet.</Empty>
                ) : (
                  <div className="flex items-end gap-px h-24 bg-surface-1 border border-surface-3 rounded p-2">
                    {data.series.map((p) => (
                      <div
                        key={p.ts}
                        className="flex-1 bg-accent rounded-sm min-w-[2px]"
                        style={{
                          height: maxSeries
                            ? `${(p.hits / maxSeries) * 100}%`
                            : "0%",
                        }}
                        title={`${new Date(p.ts).toLocaleString()} — ${p.hits}`}
                      />
                    ))}
                  </div>
                )}
              </Section>

              <Section title="Top queries">
                {data.top_queries.length === 0 ? (
                  <Empty>No queries logged yet.</Empty>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="text-[10px] uppercase tracking-wider text-zinc-500 text-left border-b border-surface-3">
                          <th className="py-2 pr-2">Query</th>
                          <th className="py-2 pr-2 text-right">Hits</th>
                          <th className="py-2 pr-2 text-right">Sessions</th>
                          <th className="py-2 pr-2 text-right">Avg results</th>
                          <th className="py-2 pr-2"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.top_queries.map((q) => (
                          <tr
                            key={q.query}
                            className="border-b border-surface-2/50 hover:bg-surface-1"
                          >
                            <td className="py-1.5 pr-2 font-mono text-accent-light truncate max-w-[180px] sm:max-w-[300px]">
                              {q.query}
                            </td>
                            <td className="py-1.5 pr-2 text-right tabular-nums">
                              {q.hits}
                            </td>
                            <td className="py-1.5 pr-2 text-right tabular-nums text-zinc-400">
                              {q.sessions}
                            </td>
                            <td className="py-1.5 pr-2 text-right tabular-nums text-zinc-500">
                              {q.avg_results.toFixed(0)}
                            </td>
                            <td className="py-1.5 pr-2 text-right">
                              <Link
                                to={`/search?q=${encodeURIComponent(q.query)}`}
                                className="text-zinc-500 hover:text-accent-light text-xs"
                              >
                                run →
                              </Link>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Section>

              <Section title="Top tags">
                {data.top_tags.length === 0 ? (
                  <Empty>
                    No tags counted yet. (Rows logged before 2026-06-03 don't
                    contribute — only queries logged after the per-tag column
                    shipped feed this chart.)
                  </Empty>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="text-[10px] uppercase tracking-wider text-zinc-500 text-left border-b border-surface-3">
                          <th className="py-2 pr-2">Tag</th>
                          <th className="py-2 pr-2 text-right">Hits</th>
                          <th className="py-2 pr-2"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.top_tags.map((t) => (
                          <tr
                            key={t.tag}
                            className="border-b border-surface-2/50 hover:bg-surface-1"
                          >
                            <td className="py-1.5 pr-2 font-mono text-accent-light truncate max-w-[180px] sm:max-w-[300px]">
                              {t.tag}
                            </td>
                            <td className="py-1.5 pr-2 text-right tabular-nums">
                              {t.hits}
                            </td>
                            <td className="py-1.5 pr-2 text-right">
                              <Link
                                to={`/search?q=${encodeURIComponent(t.tag)}`}
                                className="text-zinc-500 hover:text-accent-light text-xs"
                              >
                                run →
                              </Link>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Section>

              <Section title="By country">
                {data.countries.length === 0 ? (
                  <Empty>No data yet.</Empty>
                ) : (
                  <ul className="space-y-1">
                    {data.countries.map((c) => (
                      <li key={c.country ?? "unknown"} className="flex items-center gap-2 text-sm">
                        <span className="w-16 text-zinc-400 font-mono text-xs">
                          {c.country ?? "??"}
                        </span>
                        <div className="flex-1 bg-surface-2 rounded-sm h-3">
                          <div
                            className="bg-accent h-full rounded-sm"
                            style={{
                              width: maxCountry
                                ? `${(c.hits / maxCountry) * 100}%`
                                : "0%",
                            }}
                          />
                        </div>
                        <span className="w-12 text-right tabular-nums text-zinc-400 text-xs">
                          {c.hits}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </Section>
            </>
          )}
        </div>
      </div>
    </>
  );
}

function Tile({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-surface-1 border border-surface-3 rounded p-4">
      <div className="text-[10px] uppercase tracking-wider text-zinc-500">
        {label}
      </div>
      <div className="text-2xl font-semibold mt-1 tabular-nums">{value}</div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="text-xs uppercase tracking-wider text-zinc-500 mb-2">
        {title}
      </h2>
      {children}
    </section>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-surface-1 border border-surface-3 rounded p-6 text-center text-sm text-zinc-500">
      {children}
    </div>
  );
}
